import React, { useState, useEffect } from "react";
import { generateQuiz, BACKEND } from "../lib/api";
import { LevelLanguage, Loader, SectionTitle } from "../components/Shared";
import { Check, X, RotateCcw, Share2, MessageCircle } from "lucide-react";
import { useProfile } from "../context/ProfileContext";

const STARTERS = [
  "World mythology heroes",
  "Newton's Disc & light",
  "Magnets and electricity",
  "Pythagoras theorem",
  "Greek myths basics",
  "Aesop's fables",
];

export default function Quiz() {
  const { profile, touch, recordQuiz } = useProfile();
  const [topic, setTopic] = useState("");
  const [count, setCount] = useState(5);
  const [level, setLevel] = useState(profile.level || "school");
  const [language, setLanguage] = useState(profile.language || "english");
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [copied, setCopied] = useState(false);

  const go = async (t) => {
    const tt = (t ?? topic).trim();
    if (!tt) return;
    setLoading(true);
    setErr("");
    setQuiz(null);
    setAnswers({});
    setSubmitted(false);
    setCopied(false);
    try {
      const res = await generateQuiz({ topic: tt, count, level, language });
      setQuiz(res);
      touch();
    } catch (e) {
      setErr("Could not generate quiz. Try another topic.");
    } finally {
      setLoading(false);
    }
  };

  const score = quiz
    ? quiz.questions.reduce((s, q, i) => s + (answers[i] === q.correct_index ? 1 : 0), 0)
    : 0;

  const submit = () => {
    setSubmitted(true);
    if (quiz) recordQuiz(score, quiz.questions.length);
  };

  // when score is computed after submit, recordQuiz uses local `score` value at click time;
  // since react state updates, ensure correct: handle in effect when submitted toggles
  useEffect(() => {
    if (submitted && quiz) {
      const final = quiz.questions.reduce((s, q, i) => s + (answers[i] === q.correct_index ? 1 : 0), 0);
      recordQuiz(final, quiz.questions.length);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitted]);

  const reset = () => {
    setQuiz(null);
    setAnswers({});
    setSubmitted(false);
    setCopied(false);
  };

  const share = async () => {
    if (!quiz?.id) return;
    const url = `${BACKEND}/api/og/quiz/${quiz.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch (e) {
      window.prompt("Copy this link:", url);
    }
  };

  const shareWhatsApp = () => {
    if (!quiz?.id) return;
    const url = `${BACKEND}/api/og/quiz/${quiz.id}`;
    const text = `Try this quiz on ${quiz.topic} from EduSphere 🎯\n${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  return (
    <div className="max-w-4xl mx-auto px-5 md:px-10 py-8 md:py-12" data-testid="quiz-page">
      <SectionTitle kicker="Quiz me" title="Test what you've learned." subtitle="3–10 questions, in your language and level. Share your quiz with friends." />

      <div className="mb-5">
        <LevelLanguage level={level} setLevel={setLevel} language={language} setLanguage={setLanguage} />
      </div>

      <form
        className="grid gap-3 md:grid-cols-[1fr_auto_auto]"
        onSubmit={(e) => { e.preventDefault(); go(); }}
        data-testid="quiz-form"
      >
        <input
          className="input-field"
          placeholder="Topic — e.g. 'optics models' or 'Greek heroes'"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          data-testid="quiz-input"
        />
        <select
          className="input-field md:w-[150px]"
          value={count}
          onChange={(e) => setCount(parseInt(e.target.value))}
          data-testid="quiz-count"
        >
          <option value={3}>3 questions</option>
          <option value={5}>5 questions</option>
          <option value={7}>7 questions</option>
          <option value={10}>10 questions</option>
        </select>
        <button className="btn-primary" data-testid="quiz-generate" disabled={loading}>Generate</button>
      </form>

      <div className="flex flex-wrap gap-2 mt-4" data-testid="quiz-starters">
        {STARTERS.map((s, i) => (
          <button
            key={i}
            data-testid={`quiz-starter-${i}`}
            onClick={() => { setTopic(s); go(s); }}
            className="text-xs bg-white border border-[#DCD7CE] hover:border-[#E07A5F] px-3 py-1.5 rounded-full transition"
          >
            {s}
          </button>
        ))}
      </div>

      <div className="mt-10">
        {loading && <Loader label="Crafting questions…" />}
        {err && <div className="text-[#D9534F] text-sm" data-testid="quiz-error">{err}</div>}

        {quiz && (
          <div className="space-y-5 fadeIn" data-testid="quiz-output">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="text-[11px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold">Quiz</div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold">{quiz.topic}</h2>
              </div>
              <div className="flex gap-2">
                <button onClick={share} className="btn-outline inline-flex items-center gap-2" data-testid="quiz-share">
                  {copied ? <Check size={14} /> : <Share2 size={14} />}
                  {copied ? "Copied" : "Share"}
                </button>
                <button
                  onClick={shareWhatsApp}
                  className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white rounded-full px-4 py-2 text-sm font-semibold transition"
                  data-testid="quiz-share-whatsapp"
                  aria-label="Share on WhatsApp"
                >
                  <MessageCircle size={14} /> WhatsApp
                </button>
                <button onClick={reset} className="btn-outline inline-flex items-center gap-2" data-testid="quiz-reset">
                  <RotateCcw size={14} /> New
                </button>
              </div>
            </div>

            {quiz.questions.map((q, qi) => (
              <div key={qi} className="card p-6" data-testid={`quiz-question-${qi}`}>
                <div className="text-[11px] uppercase tracking-[0.18em] text-[#3A5A40] font-semibold mb-2">Question {qi + 1}</div>
                <div className="font-heading text-lg font-semibold text-[#1A1A1A]">{q.question}</div>
                <div className="grid sm:grid-cols-2 gap-2 mt-4">
                  {q.options.map((opt, oi) => {
                    const chosen = answers[qi] === oi;
                    const correct = q.correct_index === oi;
                    let cls = "bg-white border-[#DCD7CE] hover:border-[#E07A5F]";
                    if (submitted) {
                      if (correct) cls = "bg-[#EAECE6] border-[#3A5A40]";
                      else if (chosen && !correct) cls = "bg-[#FBE7DF] border-[#D9534F]";
                    } else if (chosen) {
                      cls = "bg-[#FBE7DF] border-[#E07A5F]";
                    }
                    return (
                      <button
                        key={oi}
                        type="button"
                        disabled={submitted}
                        onClick={() => setAnswers({ ...answers, [qi]: oi })}
                        data-testid={`quiz-option-${qi}-${oi}`}
                        className={`text-left text-sm border rounded-xl px-4 py-3 transition flex items-center justify-between gap-2 ${cls}`}
                      >
                        <span className="text-[#1A1A1A]">{opt}</span>
                        {submitted && correct && <Check size={16} className="text-[#3A5A40]" />}
                        {submitted && chosen && !correct && <X size={16} className="text-[#D9534F]" />}
                      </button>
                    );
                  })}
                </div>
                {submitted && (
                  <div className="text-sm text-[#4A4A4A] mt-3" data-testid={`quiz-explain-${qi}`}>
                    <span className="font-semibold text-[#1A1A1A]">Why: </span>{q.explanation}
                  </div>
                )}
              </div>
            ))}

            {!submitted ? (
              <button
                onClick={submit}
                disabled={Object.keys(answers).length !== quiz.questions.length}
                className="btn-secondary disabled:opacity-50"
                data-testid="quiz-submit"
              >
                Submit answers ({Object.keys(answers).length}/{quiz.questions.length})
              </button>
            ) : (
              <div className="card p-6 flex items-center justify-between flex-wrap gap-4" data-testid="quiz-score">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.18em] text-[#3A5A40] font-semibold">Your score</div>
                  <div className="font-heading text-3xl font-bold text-[#1A1A1A]">{score} / {quiz.questions.length}</div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={share} className="btn-outline inline-flex items-center gap-2">
                    <Share2 size={14} /> Share
                  </button>
                  <button
                    onClick={shareWhatsApp}
                    className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1FB855] text-white rounded-full px-4 py-2 text-sm font-semibold transition"
                  >
                    <MessageCircle size={14} /> WhatsApp
                  </button>
                  <button onClick={reset} className="btn-primary">Try another</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
